import {FC, useState} from 'react'
import {useDeleteAgendaEvent} from '@/features/agenda/queries/useDeleteAgendaEvent'
import {DialogStore} from '@ariakit/react'
import {useAgendaAppointmentEventData} from '@/features/agenda/hooks/useAgendaAppointmentEventData'
import {toast} from '@/utilities/toast/toast'
import {useTranslation} from '@/translations/i18n'
import {errorHandler} from '@/utilities/helpers'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {zodResolver} from '@hookform/resolvers/zod'
import {FormProvider, useForm} from 'react-hook-form'
import {AgendaEventTreatmentSchema, DeleteAgendaEventFormValidationSchema} from '../../formSchemas'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import type {AgendaEventAppointment} from '@/features/agenda/types'
import {StyledTargetDeletionNameText} from '@/components/commons/delete-dialog/style'
import {Switch} from '@/components/commons/switch/Switch'
import {EventRepetitionFrequencyText} from '@/features/agenda/components/event-repetition-frequency-text/EventRepetitionFrequencyText'
import {AxiosError} from 'axios'

interface DeleteAgendaEventConfirmationDialogProps {
    event: AgendaEventAppointment
    dialogStore: DialogStore
    onDeletionSuccess: () => void
    repetitionData?: AgendaEventTreatmentSchema['repetitionData']
}

export const DeleteAgendaEventConfirmationDialog: FC<DeleteAgendaEventConfirmationDialogProps> = ({
    event,
    dialogStore,
    onDeletionSuccess,
    repetitionData
}) => {
    const {t} = useTranslation()
    const [applyToFutureEvents, setApplyToFutureEvents] = useState(false)

    const form = useForm<DeleteAgendaEventFormValidationSchema>({
        resolver: zodResolver(DeleteAgendaEventFormValidationSchema),
        defaultValues: {
            notificationsRecipients: event.eventConsultants?.map(consultant => ({
                id: consultant.userId,
                fullName: consultant.user.fullName
            }))
        }
    })

    // Get the event display name
    const {fullNames, accessTypes} = useAgendaAppointmentEventData({agendaEvent: event})
    const targetNameToDelete = `${accessTypes} - ${fullNames}`

    // Use the delete agenda event mutation
    const deleteEventMutation = useDeleteAgendaEvent({
        urlParams: {eventId: event.id},
        params: {applyToFutureEvents},
        options: {
            onSuccess: () => {
                toast.success(
                    <Flexbox direction="column">
                        <Text size="textSm" weight="500">
                            {t('agenda:event_deleted')}
                        </Text>
                        <Text size="textSm" palette="neutral" shade="500">
                            {targetNameToDelete}
                        </Text>
                    </Flexbox>
                )

                onDeletionSuccess()
                dialogStore.hide()
            },
            onError: error => {
                dialogStore.hide()
                if (error instanceof AxiosError && error?.response?.status == 400) {
                    toast.error(
                        <Flexbox direction="column">
                            <Text size="textSm" weight="500">
                                {t('agenda:error_delete_title')}
                            </Text>
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('agenda:error_400_delete_subtitle')}
                            </Text>
                        </Flexbox>
                    )

                    return
                }

                if (error instanceof AxiosError && error?.response?.status == 404) {
                    toast.error(
                        <Flexbox direction="column">
                            <Text size="textSm" weight="500">
                                {t('agenda:error_delete_title')}
                            </Text>
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('agenda:error_404_delete_subtitle')}
                            </Text>
                        </Flexbox>
                    )

                    return
                }

                errorHandler(error)
            }
        }
    })

    const onSubmit = form.handleSubmit((data: DeleteAgendaEventFormValidationSchema) => {
        deleteEventMutation.mutate({
            notificationRecipientIds: data.notificationsRecipients?.map(recipient => recipient.id),
            notificationDetails: data.notificationDetails
        })
    })

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={onSubmit}
                isConfirmPending={deleteEventMutation.isPending}
                confirmButtonVariant="primaryDanger"
                contentSlot={
                    !!event.frequency && (
                        <Flexbox direction="column" gap={2}>
                            <Switch
                                size="sm"
                                label={t('agenda:apply_to_future_events')}
                                checked={applyToFutureEvents}
                                onChange={() => setApplyToFutureEvents(prev => !prev)}
                            />
                            {applyToFutureEvents &&
                                repetitionData &&
                                repetitionData.frequency &&
                                repetitionData.position &&
                                repetitionData.dayOfWeek && (
                                    <EventRepetitionFrequencyText
                                        frequency={repetitionData.frequency}
                                        position={repetitionData.position}
                                        dayOfWeek={repetitionData.dayOfWeek}
                                    />
                                )}
                        </Flexbox>
                    )
                }
                headerSlot={
                    <Flexbox direction="column" gap={2}>
                        <DialogHeading>{t('agenda:event_delete_confirmation_title')}</DialogHeading>
                        <div>
                            <Text palette="neutral" shade="600" size="textSm">
                                {t('commons:delete_paragraph_1')}
                            </Text>
                            <StyledTargetDeletionNameText palette="neutral" shade="950" size="textSm" weight="500">
                                {targetNameToDelete}
                            </StyledTargetDeletionNameText>
                            <Text palette="neutral" shade="600" size="textSm">
                                {t('commons:delete_paragraph_2')}
                            </Text>
                        </div>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
